import { useFormik, FormikProvider, getIn } from "formik";
import * as Yup from "yup";
import { useContext, useEffect, useMemo, useState } from "react";
import { isCPF } from "../../../../common/helpers/is-cpf";
import { ILead, SITUATION_ORIGIN_LEADS } from "../../../../types/lead.type";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import InputFormik from "../../../../common/components/form/InputFormik";
import {
  maskCNH,
  maskCPF,
  maskPhone,
} from "../../../../common/helpers/mask.helpers";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { DocumentRowLead } from "./fields/DocumentRowLead";
import AddressFieldsLeads from "./fields/AddressFiledsLeads";
import ReferenceFieldsLeads from "./fields/ReferenceFieldsLeads";
import TextareaFormik from "../../../renters/components/forms/TextareaFormik";
import { useCompanyStore } from "../../../../common/states/store/Company";
import PuxaCapivaraActivate from "./fields/PuxaCapivaraActivate";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { getSdrOptions } from "../../../../common/helpers/get-sdr";
import { ConsultantOption } from "@/types/employee.type";
import { getConsultantOptionsFromAPI } from "@/common/services/employee.api";
import { Button } from "@/components/ui/button";
import { SearchCheck } from "lucide-react";
import { getLeadPersonDataByCPF } from "@/common/services/leads.api";
import { emptyLead } from "@/types/lead.type";

type LeadPersonDataByCPF = {
  nome?: string;
  genero?: string;
  data_nascimento?: string;
};

// Esquema de validação com Yup
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .test(
      "full-name",
      "Deve incluir pelo menos um nome e um sobrenome",
      (value) => {
        if (!value) return false;
        const parts = value.split(" ");
        return parts.length >= 2;
      }
    ),
  whatsapp: Yup.string().required("Número do WhatsApp obrigatório"),
  birthDate: Yup.date()
    .required("Data de nascimento é obrigatória")
    .max(new Date(), "Data de nascimento não pode ser no futuro"),
  cnh: Yup.string().required("CNH é obrigatória"),
  //.test('valida-cnh', 'CNH inválida', value => isCNH(value)),
  categoryCNH: Yup.string().required("Tipo de CNH é obrigatório"),
  cpf: Yup.string()
    .required("CPF é obrigatório")
    .test("valida-cpf", "CPF inválido", (value) => isCPF(value)),
  rg: Yup.string().required("Número do RG é obrigatório"),
  rg_orgexp: Yup.string().required("Orgão expedidor é obrigatório"),
  leadOrigin: Yup.string().required("Origem do Lead é obrigatória"),
  cpfDoIndicador: Yup.string().when("cpfDoIndicadorOptions.indicated", {
    is: true,
    then: (schema) =>
      schema
        .required("CPF do Indicador é obrigatório")
        .test("valida-cpf", "CPF inválido", (value) => isCPF(value)),
  }),
});

type FormPropsCreateLead = {
  onSubmit: (
    values: ILead
  ) => Promise<{ ok: boolean; data: ILead; errors?: unknown[] }>;
  data: ILead;
  formRequestErrors?: { message: string; path: string[] }[];
};

function FormEditLead(props: FormPropsCreateLead) {
  const authCtx = useContext(AuthContext);
  const userEmail = authCtx.auth?.user?.email;
  const { filterMode, selectedCompanies, selectedRegion, selectedUf } =
    useCompanyStore();

  const { notifyError, notifySuccess } = useToastContext();
  const normalizeLeadForForm = (leadData: ILead): ILead => {
    const base = emptyLead();

    return {
      ...base,
      ...leadData,
      companyId: leadData?.companyId || base.companyId,
      documents: Array.isArray(leadData?.documents) ? leadData.documents : base.documents,
      references: Array.isArray(leadData?.references) ? leadData.references : base.references,
      observations: Array.isArray(leadData?.observations) ? leadData.observations : base.observations,
      pendencies: Array.isArray(leadData?.pendencies) ? leadData.pendencies : base.pendencies,
      address: leadData?.address || base.address,
      criminalCheckLead: leadData?.criminalCheckLead || base.criminalCheckLead,
      cpfDoIndicadorOptions: leadData?.cpfDoIndicadorOptions || base.cpfDoIndicadorOptions,
      jurValidation: leadData?.jurValidation || base.jurValidation,
      externalReferences: leadData?.externalReferences || base.externalReferences,
    };
  };

  const safeLeadData = normalizeLeadForForm(props.data);
  const [theInitialData, setNewInitialData] = useState<ILead>(
    Object.assign(safeLeadData, {
      birthDate: (safeLeadData.birthDate || "").split("T")[0],
      cnhExpirationDate: (safeLeadData.cnhExpirationDate || "").split("T")[0],
      documents: safeLeadData.documents.map((doc) =>
        Object.assign(doc, { file: undefined })
      ),
      criminalCheckLead: {
        jus: safeLeadData?.criminalCheckLead?.jus || false,
        escavador: safeLeadData?.criminalCheckLead?.escavador || false,
      },
      cpfDoIndicadorOptions: {
        indicated: safeLeadData?.cpfDoIndicadorOptions?.indicated || false,
        notIndicated: safeLeadData?.cpfDoIndicadorOptions?.notIndicated || false,
      },
      jurValidation: {
        status: safeLeadData?.jurValidation?.status || false,
        validated: safeLeadData?.jurValidation?.validated || false,
        details: safeLeadData?.jurValidation?.details || "",
      },
      observations: safeLeadData.observations || [
        { text: "", date: new Date().toISOString(), author: "" },
      ],
    })
  );
  const [validCPF, setValidCPF] = useState(false);
  const [isFetchingPersonDataByCPF, setIsFetchingPersonDataByCPF] =
    useState(false);
  const [isPersonDataLockedByCPF, setIsPersonDataLockedByCPF] = useState(false);
  const [autofilledCPF, setAutofilledCPF] = useState("");
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);
  const formik = useFormik({
    initialValues: theInitialData,
    validationSchema: validationSchema,
    onSubmit: async (data: ILead) => {
      data.observations.forEach((observation) => {
        if (!observation.date) {
          observation.date = new Date().toISOString();
        }

        if (!observation.author) {
          // Only set the author if it hasn't been set yet
          observation.author = userEmail || "";
        }
      });

      const result = await props.onSubmit(data);
      if (result.ok) {
        const normalizedResult = normalizeLeadForForm(result.data);
        formik.setValues(normalizedResult);
        setNewInitialData(normalizedResult);
        notifySuccess("Atualizado com sucesso.");
      }
      return result;
    },
  });
  const singleSelectedCompany = useMemo(() => {
    if (filterMode === "cities" && selectedCompanies.length === 1) {
      return selectedCompanies[0];
    }
    return null;
  }, [filterMode, selectedCompanies]);

  useEffect(() => {
    if (singleSelectedCompany) {
      formik.setFieldValue("groupName", singleSelectedCompany.city);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSelectedCompany]);
  const observations = getIn(formik.values, "observations");
  const currentObservation = useMemo(() => {
    const total = (Array.isArray(observations) ? observations : []).filter(({ date }) => !!date);
    return total.length;
  }, [observations]);

  const age = useMemo(() => {
    if (formik.values.birthDate) {
      const birthDate = new Date(formik.values.birthDate);
      const today = new Date();

      let calculatedAge = today.getFullYear() - birthDate.getFullYear();

      // Se o mês atual for menor que o mês de nascimento ou for o mesmo mês mas o dia ainda não ocorreu, subtrai 1 da idade
      if (
        today.getMonth() < birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() &&
          today.getDate() < birthDate.getDate())
      ) {
        calculatedAge--;
      }

      return calculatedAge;
    }

    return null;
  }, [formik.values.birthDate]);

  const [, setCurrentDate] = useState(new Date().toISOString().split("T")[0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date().toISOString().split("T")[0]);
    }, 60000);
    return () => clearInterval(interval); // Limpa o intervalo quando o componente é desmontado
  }, []);

  // Watch for changes in CPF field to enable/disable PuxaCapivaraValidationLead
  useEffect(() => {
    const validCPF = isCPF(formik.values.cpf);
    setValidCPF(validCPF);
  }, [formik.values.cpf]);

  useEffect(() => {
    const sanitizedCPF = formik.values.cpf.replace(/\D/g, "");

    if (autofilledCPF && sanitizedCPF !== autofilledCPF) {
      setIsPersonDataLockedByCPF(false);
      setAutofilledCPF("");
    }
  }, [autofilledCPF, formik.values.cpf]);

  // const invalidCpf = getIn(formik.errors, 'cpfDoIndicador');
  const leadOriginOptions = useMemo(() => {
    return [
      { value: "-", label: "-" },
      ...Object.entries(SITUATION_ORIGIN_LEADS).map(([key, value]) => ({
        value: key,
        label: value,
      })),
    ];
  }, []);

  const savedSDR = formik.values.sdr;

  const sdrOptions = getSdrOptions(savedSDR);
  useEffect(() => {
    const fetchConsultants = async () => {
      let companyFilter: string[] | undefined;
      let ufFilter: string | null = null;
      if (filterMode === "cities" && selectedCompanies.length > 0) {
        companyFilter = selectedCompanies.map((c) => c.companyId);
      } else if (filterMode === "uf") {
        ufFilter = selectedUf;
      }

      const options = await getConsultantOptionsFromAPI(
        companyFilter,
        ufFilter || undefined
      );

      // 4. Garante que o valor já salvo seja mantido na lista
      const savedValue = props.data.technicalConsultant;
      if (savedValue && !options.some((opt) => opt.value === savedValue)) {
        options.unshift({ value: savedValue, label: savedValue });
      }

      setConsultantOptions(options);
    };

    fetchConsultants();
  }, [
    filterMode,
    selectedCompanies,
    selectedRegion,
    selectedUf,
    props.data.technicalConsultant,
  ]);
  const clearPersonDataFields = () => {
    formik.setFieldValue("name", "");
    formik.setFieldValue("gender", "");
    formik.setFieldValue("birthDate", "");
    setIsPersonDataLockedByCPF(false);
    setAutofilledCPF("");
  };

  const mapGenderValue = (gender?: string) => {
    const normalizedGender = (gender || "").trim().toUpperCase();

    if (normalizedGender === "M") return "male";
    if (normalizedGender === "F") return "female";
    if (normalizedGender) return "other";

    return "";
  };

  const getPersonDataByCPF = async (cpf: string) => {
    if (!isCPF(cpf)) {
      notifyError("Informe um CPF valido para realizar a busca.");
      clearPersonDataFields();
      return;
    }

    const sanitizedCPF = cpf.replace(/\D/g, "");

    try {
      setIsFetchingPersonDataByCPF(true);

      const response = (await getLeadPersonDataByCPF(sanitizedCPF)) as unknown as {
        ok: boolean;
        data?: LeadPersonDataByCPF[] | string | null;
      };

      const personData = Array.isArray(response.data) ? response.data[0] : null;

      if (response.ok && personData) {
        formik.setFieldValue("name", personData.nome || "");
        formik.setFieldValue("gender", mapGenderValue(personData.genero));
        formik.setFieldValue(
          "birthDate",
          (personData.data_nascimento || "").split("T")[0]
        );
        setIsPersonDataLockedByCPF(true);
        setAutofilledCPF(sanitizedCPF);
        return;
      }

      clearPersonDataFields();
    } catch (error) {
      setIsPersonDataLockedByCPF(false);
      setAutofilledCPF("");
      notifyError("Nao foi possivel buscar os dados do CPF, sera necessario preencher manualmente.");
      console.error(error);
    } finally {
      setIsFetchingPersonDataByCPF(false);
    }
  };


  return (
    <form className="form-edit" onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="section-name">Informações Pessoais</h2>

          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Primeira linha */}
            <div className="style-informacoes-pessoais">
              <InputFormik
                label="CPF:"
                name="cpf"
                placeholder="111.222.333-44"
                required={true}
                mask={maskCPF}
                className="w-full"
                actionButton={
                  <Button
                    variant={"destructive"}
                    type="button"
                    size="icon"
                    disabled={!validCPF || isFetchingPersonDataByCPF}
                    onClick={() => getPersonDataByCPF(formik.values.cpf)}
                  >
                    <SearchCheck />
                  </Button>
                }
              />


              <InputFormik
                label="Nome completo:"
                name="name"
                className=""
                required={true}
                readonly={isPersonDataLockedByCPF}
              />

              <ComboBoxFormik
                label="Gênero:"
                name="gender"
                options={[
                  { value: "", label: "-" },
                  { value: "male", label: "Masculino" },
                  { value: "female", label: "Feminino" },
                  { value: "other", label: "Outro" },
                ]}
                required={true}
                disabled={isPersonDataLockedByCPF}
              />

              <InputFormik
                label="E-mail:"
                name="email"
                type="email"
                required={true}
              />

              <InputFormik
                label="WhatsApp:"
                name="whatsapp"
                type="whatsapp"
                required={true}
                mask={maskPhone}
              />


              <InputFormik
                label="Nome da mãe:"
                name="motherName"
                required={true}
              />

              <InputFormik
                label={`Data de nascimento (${age} anos):`}
                name="birthDate"
                type="date"
                required={true}
                readonly={isPersonDataLockedByCPF}
              />

              <ComboBoxFormik
                label="Estado civil:"
                name="maritalStatus"
                options={[
                  { value: "", label: "-" },
                  { value: "Solteiro", label: "Solteiro" },
                  { value: "Casado", label: "Casado" },
                  { value: "Separado", label: "Separado" },
                  { value: "Divorciado", label: "Divorciado" },
                  { value: "Viúvo", label: "Viúvo" },
                  { value: "Amasiado", label: "Amasiado" },
                ]}
                required={true}
              />

              <ComboBoxFormik
                label="Tipo sanguíneo:"
                name="typeSanguine"
                options={[
                  { value: "-", label: "-" },
                  { value: "O-", label: "O-" },
                  { value: "O+", label: "O+" },
                  { value: "A-", label: "A-" },
                  { value: "A+", label: "A+" },
                  { value: "B-", label: "B-" },
                  { value: "B+", label: "B+" },
                  { value: "AB-", label: "AB-" },
                  { value: "AB+", label: "AB+" },
                ]}
                required={true}
              />

              <InputFormik
                label="CNH:"
                name="cnh"
                placeholder="00000000"
                required={true}
                mask={maskCNH}
              />

              <InputFormik
                label="Data de vencimento da CNH:"
                name="cnhExpirationDate"
                type="date"
                required={true}
              />

              <ComboBoxFormik
                label="Categoria CNH:"
                name="categoryCNH"
                options={[
                  { value: "-", label: "-" },
                  { value: "A", label: "A" },
                  { value: "B", label: "B" },
                ]}
                required={true}
              />

              <InputFormik label="RG:" name="rg" required={true} />

              <InputFormik
                label="Orgão expedidor:"
                name="rg_orgexp"
                required={true}
              />
              <ComboBoxFormik
                label="Origem do Lead:"
                name="leadOrigin"
                options={leadOriginOptions}
                required={true}
              />

              <ComboBoxFormik
                label="Consultor:"
                name="technicalConsultant"
                options={consultantOptions}
                required={true}
                disabled={formik.values.technicalConsultant !== ""}
              />

              <ComboBoxFormik
                label="Representante de vendas (SDR):"
                name="sdr"
                options={sdrOptions}
                required={true}
                disabled={formik.values.sdr !== ""}
              />
            </div>
          </div>
        </section>

        <section>
          <PuxaCapivaraActivate
            entityId={props.data._id || ""}
            entityName="Lead"
            validCPF={validCPF}
            cpf={formik.values.cpf}
            status={formik.values.jurValidation.status}
            validated={formik.values.jurValidation.validated}
            whatsapp={formik.values.whatsapp}
          />
        </section>

        {/* <section>
          <CpfDoIndicadorCheck invalidCpf={invalidCpf} />
        </section> */}

        <section className="bg-gray2/20 mt-5 mb-10 p-3 rounded-md shadow">
          <h2 className="section-name">Endereço</h2>
          <AddressFieldsLeads />
        </section>

        <section>
          <h2 className="section-name">Referências</h2>
          {theInitialData.references.map((ref, index) => (
            <ReferenceFieldsLeads key={index} index={index} reference={ref} />
          ))}
        </section>

        <section className="mt-5 mb-10 p-3">
          <h2 className="section-name">Documentos</h2>

          <div className="flex flex-col items-stretch">
            {formik.values?.documents?.map((doc, index) => (
              <div
                key={index}
                className={`flex flex-col space-y-2 p-3 my-0 rounded ${index % 2 ? "bg-gray/5" : "bg-gray/25"
                  }`}
              >
                <DocumentRowLead
                  name={`documents.${index}`}
                  initialData={doc || {}}
                />
              </div>
            ))}
            <button
              type="button"
              className="button-adicionar-documento"
              onClick={() => {
                formik.setFieldValue(
                  `documents.${formik.values.documents.length}`,
                  { type: "", content: "", verified: false }
                );
              }}
            >
              + Adicionar Documento
            </button>
          </div>
        </section>

        <section>
          <div className="style-observacoes">
            <h2 className="font-semibold text-2xl mb-4">Observações</h2>
            <div>
              <TextareaFormik
                label={`Observação`}
                name={`observations.${currentObservation}.text`}
                className="min-h-64 w-full rounded shadow-md focus:border-white"
              />
            </div>
          </div>
        </section>

        <div className="w-full flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditLead;
